// The links page (/store/links): the one page QR codes and social profiles point at. A full-screen photo,
// the logo, and a short stack of buttons. What it shows is the "links" part of the shop's saved content.
import React, { useEffect } from 'react';
import { ArrowUpRight, ChevronRight, Instagram, Link2, Mail, ShoppingBag, Tag } from 'lucide-react';
import { track } from './api.js';
import { Picture } from './ui.jsx';
import './links.css';

const LOGO = '/icons/3dlabs-real-logo-192.png?v=4.2.0-logo4';
const inside = (url) => url.startsWith('/');
const iconFor = (url) => inside(url) ? ShoppingBag : url.startsWith('mailto:') ? Mail : /^https:\/\/((www\.)?instagram\.com|ig\.me)\//i.test(url) ? Instagram : Link2;

function LinkButton({ item, primary = false, i, onOpen }) {
  const Icon = iconFor(item.url), Arrow = inside(item.url) ? ChevronRight : ArrowUpRight;
  // A tap always opens the link: counting is fire-and-forget and can never stop it. Outside links (Instagram,
  // Printables...) open in the same tab, because Instagram's in-app browser ignores links that ask for a new tab,
  // and a same-tab tap is what lets the phone hand ig.me / instagram.com over to the Instagram app.
  const open = (event) => {
    try { track('store_link_click', null, { label: item.label, url: item.url, source: 'links_page' }); } catch { /* never blocks */ }
    if (inside(item.url)) { event.preventDefault(); onOpen(item.url); }
  };
  return <a className={`links-button ${primary ? 'is-primary' : ''}`} style={{ '--i': i }} href={item.url} onClick={open} rel={item.url.startsWith('https://') ? 'noreferrer' : undefined}>
    <Icon className="links-icon" aria-hidden="true" />
    <span className="links-label"><strong>{item.label}</strong>{item.note && <small>{item.note}</small>}</span>
    <Arrow className="links-arrow" aria-hidden="true" />
  </a>;
}

export default function LinksPage({ links, mobile, onOpen }) {
  // A printed QR code sends its visitors here with ?offer, and only they are shown the offer.
  const offered = new URLSearchParams(location.search).has('offer') && links.offer?.title ? links.offer : null;
  useEffect(() => { track('store_links_open', null, { source: offered ? 'offer' : 'direct' }); }, []);
  const photo = mobile ? links.image_mobile || links.image : links.image || links.image_mobile;
  return <div className="links-page">
    <Picture className="links-photo" src={photo} sizes="100vw" priority />
    <main className="links-card">
      <img className="links-logo" src={LOGO} alt="" width="96" height="96" />
      <h1>{links.title}</h1>
      {links.text && <p className="links-text">{links.text}</p>}
      {offered && <section className="links-offer" aria-label="Offer">
        <span className="links-offer-tag"><Tag aria-hidden="true" />Special offer</span>
        <h2>{offered.title}</h2>
        {offered.text && <p>{offered.text}</p>}
        {offered.url && <a className="shop-btn light wide" href={offered.url} onClick={() => { try { track('store_link_click', null, { label: offered.button || 'offer', url: offered.url, source: 'links_offer' }); } catch { /* never blocks */ } }}>{offered.button || 'Claim the offer'}</a>}
      </section>}
      <nav className="links-list" aria-label="Links">
        {links.items.map((item, index) => <LinkButton key={`${item.url}-${index}`} item={item} primary={index === 0} i={index} onOpen={onOpen} />)}
      </nav>
      <footer className="links-foot">
        <span>© {new Date().getFullYear()} 3DLabs. Designed and made in Ontario.</span>
        <a href="/store/privacy" onClick={(event) => { event.preventDefault(); onOpen('/store/privacy'); }}>Privacy</a>
      </footer>
    </main>
  </div>;
}
